import { inject, Injectable } from '@angular/core';
import { from, map, Observable, switchMap, tap } from 'rxjs';
import { EingangCtBatch, PsNavigationService } from '@ps-gui/claims-terminal-shared';
import { AuditOrderTransmissionService } from './audit-order-transmission.service';
import { EingangCompletionBuilder } from './eingang-completion.builder';
import { CtBatchExtractionApiService } from '../api/ct-batch-extraction-api.service';
import { PersistDataService } from '../app-state/persist-data.service';
import { GUTACHTEN_AUFTRAGSARTEN_STRINGS } from '@ps-gui/shared-core';

/**
 * Handles transmission for `EingangCtBatch`: pushes the consolidated data + per-document
 * extracted-data payload to the CT backend (`/api/batch/{batchId}/eingang/ct2`), then
 * orders a new audit via the Personenschaden API and delegates the shared finalize block
 * to `AuditCtBatchTransmissionService`.
 *
 * When `psBackendWeiterleitungEnabled` is set, the dispatcher routes to
 * `PsServerCtBatchTransmissionService` instead, so this service always performs the direct
 * ps-backend order.
 */
@Injectable({ providedIn: 'root' })
export class EingangCtBatchTransmissionService {
  private readonly auditOrder = inject(AuditOrderTransmissionService);
  private readonly ctBatchExtractionApiService = inject(CtBatchExtractionApiService);
  private readonly persistDataService = inject(PersistDataService);
  private readonly psNavigationService = inject(PsNavigationService);
  private readonly completionBuilder = inject(EingangCompletionBuilder);

  transmitAndRedirect(ctBatch: EingangCtBatch): Observable<unknown> {
    return from(this.completionBuilder.loadSnapshotAndBuildDto(ctBatch.metaData)).pipe(
      switchMap(({ snapshot, dto }) =>
        this.ctBatchExtractionApiService
          .completeEingangCt2(ctBatch.id, dto)
          .pipe(map((response) => ({ response, snapshot, dto }))),
      ),
      switchMap(({ response, dto }) => {
        const auftragsart = dto.consolidatedData.auftragsart;

        if (GUTACHTEN_AUFTRAGSARTEN_STRINGS.has(auftragsart)) {
          return this.ctBatchExtractionApiService.signalCtBatchCt2Completion(ctBatch.id).pipe(
            tap(async () => {
              await this.persistDataService.reset();
              this.psNavigationService.navigateToGutachten(response.auftragIds[0]);
            }),
          );
        }

        return this.auditOrder.transmitAndRedirect(ctBatch);
      }),
    );
  }
}
